import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import './GroupManager.css';

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function GroupManager({ contacts, onGroupCreated, compact = false }) {
  const [groups, setGroups] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await api.get('/api/groups');
      if (response.data.success) {
        setGroups(response.data.groups);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const handleContactToggle = (contactId) => {
    setSelectedContacts(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts.map(c => c.id));
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!groupName.trim()) {
      setError('Group name is required');
      setLoading(false);
      return;
    }

    if (selectedContacts.length === 0) {
      setError('Please select at least one contact');
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/api/groups', {
        name: groupName.trim(),
        description: groupDescription.trim() || null,
        contact_ids: selectedContacts
      });

      if (response.data.success) {
        setSuccess('Group created successfully!');
        setGroupName('');
        setGroupDescription('');
        setSelectedContacts([]);
        setShowCreateModal(false);
        fetchGroups();
        if (onGroupCreated) {
          onGroupCreated();
        }
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group?')) {
      return;
    }

    try {
      const response = await api.delete(`/api/groups/${groupId}`);
      if (response.data.success) {
        setSuccess('Group deleted successfully!');
        fetchGroups();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete group');
    }
  };

  return (
    <div className="group-manager">
      <div className="group-manager-header">
        <h2>📁 Contact Groups</h2>
        <button 
          className="create-group-btn"
          onClick={() => setShowCreateModal(true)}
        >
          + Create Group
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>⚠️</span> {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span>✓</span> {success}
        </div>
      )}

      {groups.length === 0 ? (
        <div className="no-groups">
          <p>No groups created yet. Create your first group!</p>
        </div>
      ) : (
        <div className="groups-list">
          {groups.map(group => (
            <div key={group.id} className="group-card">
              <div className="group-card-header">
                <div>
                  <h3>{group.name}</h3>
                  {group.description && (
                    <p className="group-description">{group.description}</p>
                  )}
                </div>
                <button
                  className="delete-group-btn"
                  onClick={() => handleDeleteGroup(group.id)}
                  title="Delete group"
                >
                  🗑️
                </button>
              </div>
              <div className="group-info">
                <span className="contact-count">
                  {group.contact_count || 0} {group.contact_count === 1 ? 'contact' : 'contacts'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Group</h3>
              <button 
                className="close-modal-btn"
                onClick={() => setShowCreateModal(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateGroup} className="group-form">
              <div className="form-group">
                <label htmlFor="groupName">Group Name *</label>
                <input
                  type="text"
                  id="groupName"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Enter group name"
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="groupDescription">Description (Optional)</label>
                <textarea
                  id="groupDescription"
                  value={groupDescription}
                  onChange={(e) => setGroupDescription(e.target.value)}
                  placeholder="Enter group description"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <div className="select-contacts-header">
                  <label>Select Contacts *</label>
                  <button
                    type="button"
                    className="select-all-btn"
                    onClick={handleSelectAll}
                  >
                    {selectedContacts.length === contacts.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="contacts-selection">
                  {contacts.length === 0 ? (
                    <p className="no-contacts-msg">No contacts available. Please add contacts first.</p>
                  ) : (
                    contacts.map(contact => (
                      <label key={contact.id} className="contact-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedContacts.includes(contact.id)}
                          onChange={() => handleContactToggle(contact.id)}
                        />
                        <span className="contact-info">
                          <strong>{contact.name}</strong>
                          <span className="contact-phone">{contact.phone_number}</span>
                        </span>
                      </label>
                    ))
                  )}
                </div>
                {selectedContacts.length > 0 && (
                  <p className="selected-count">
                    {selectedContacts.length} {selectedContacts.length === 1 ? 'contact' : 'contacts'} selected
                  </p>
                )}
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowCreateModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="create-btn"
                  disabled={loading || selectedContacts.length === 0}
                >
                  {loading ? 'Creating...' : 'Create Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default GroupManager;

